import { useEffect } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  ArrowLeft,
  BadgeCheck,
  ChevronRight,
  Copy,
  Loader2,
  Mail,
  MessageCircle,
  Phone,
  Trash2,
} from "lucide-react";

import { AdminCard, ListState, StatusPill } from "@/components/admin/primitives";
import { FinanceCard } from "@/components/admin/pedidos/FinanceCard";
import { ItemPreview } from "@/components/ItemPreview";
import { OrderPdfButton } from "@/components/OrderPdfButton";
import { PreviewStage } from "@/components/PreviewStage";
import { UnitGridView } from "@/components/UnitGridView";
import { formatDate, formatDateTime } from "@/lib/dates";
import { countLabel, formatBRL, padOrderNumber } from "@/lib/format";
import { ORDER_STATUSES, ORDER_STATUS_LABEL, customerKey, whatsappUrl } from "@/lib/orders";
import {
  useAdminOrder,
  useCustomer,
  useCustomerOrders,
  useDeleteOrder,
  useDuplicateOrder,
  useMarkOrderSeen,
  useUpdateOrderStatus,
} from "@/lib/queries";
import { PAYMENT_METHODS } from "@/lib/finance";
import { useSession } from "@/lib/session";
import { cn } from "@/lib/utils";

/** Pedido aberto: tudo do pedido a esquerda, tudo do cliente a direita. */
export const Route = createFileRoute("/admin/_painel/pedidos/$pedidoId")({
  head: () => ({ meta: [{ title: "Pedido — Painel MonteBox" }] }),
  component: AdminPedido,
});

/**
 * A equipe pode pôr o pedido em qualquer etapa — inclusive pular as do
 * cliente, num pedido fechado por telefone.
 */
const STATUS_STEPS = ORDER_STATUSES;
const STATUS_LABEL = ORDER_STATUS_LABEL;

function AdminPedido() {
  const { pedidoId } = Route.useParams();
  const navigate = useNavigate();

  const order = useAdminOrder(pedidoId);
  const key = order.data ? customerKey(order.data.customer.whatsapp) : "";
  const customer = useCustomer(key);
  const customerOrders = useCustomerOrders(key);

  const { profile } = useSession();
  const isAdmin = profile?.role === "ADMIN";
  const updateStatus = useUpdateOrderStatus();
  const duplicate = useDuplicateOrder();
  const remove = useDeleteOrder();

  // Abrir o pedido é o que tira o destaque de "novo" da lista e do menu.
  const markSeen = useMarkOrderSeen();
  const unseen = order.data !== undefined && order.data.status !== "rascunho" && !order.data.seenAt;
  useEffect(() => {
    if (unseen) markSeen.mutate(pedidoId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unseen, pedidoId]);

  if (order.isLoading || !order.data) {
    return (
      <AdminCard className="m-8">
        <ListState
          loading={order.isLoading}
          error={order.error}
          empty={!order.isLoading && !order.data}
          emptyLabel="Pedido não encontrado."
        />
      </AdminCard>
    );
  }

  const data = order.data;
  const pieces = data.items.reduce((sum, item) => sum + item.quantity, 0);
  const otherOrders = (customerOrders.data?.items ?? []).filter((o) => o.id !== data.id);

  const handleDuplicate = async () => {
    const copy = await duplicate.mutateAsync(data.id);
    toast.success(`Pedido duplicado como #${padOrderNumber(copy.number)}`);
    await navigate({ to: "/admin/pedidos/$pedidoId", params: { pedidoId: copy.id } });
  };

  const handleDelete = async () => {
    // Excluir um pedido nao tem desfazer — a confirmacao e a unica rede.
    if (!window.confirm(`Excluir o pedido #${padOrderNumber(data.number)}?`)) return;
    await remove.mutateAsync(data.id);
    toast.success("Pedido excluído.");
    await navigate({ to: "/admin/pedidos" });
  };

  return (
    <>
      <header className="flex min-h-16 shrink-0 flex-wrap items-center justify-between gap-3 border-b border-mb-line bg-white px-4 py-3 sm:px-8">
        <div className="flex items-center gap-3">
          <Link
            to="/admin/pedidos"
            aria-label="Voltar para a lista de pedidos"
            className="grid h-9 w-9 place-items-center rounded-full border border-mb-line text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface"
          >
            <ArrowLeft className="h-[18px] w-[18px]" />
          </Link>
          <Link to="/admin/pedidos" className="text-sm font-semibold text-mb-muted">
            Pedidos
          </Link>
          <ChevronRight className="h-4 w-4 text-mb-muted" />
          <h1 className="font-display text-xl font-bold tracking-tight text-mb-navy">
            #{padOrderNumber(data.number)}
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* A ficha vai para a produção: sem valores, com a arte de cada peça. */}
          <OrderPdfButton order={data} kind="producao" label="Ficha de produção" />
          <OrderPdfButton order={data} kind="pedido" label="PDF do pedido" />
          <button
            type="button"
            onClick={() => void handleDuplicate()}
            disabled={duplicate.isPending}
            className="flex items-center gap-1.5 rounded-full border border-mb-line bg-white px-3.5 py-2 text-xs font-bold text-mb-navy transition-colors hover:border-mb-navy hover:bg-mb-surface disabled:opacity-50"
          >
            {duplicate.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
            Duplicar
          </button>
          {/* Venda paga não se exclui — cancela, e o cancelamento fica registrado. */}
          {!data.payment && (
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={remove.isPending}
              className="flex items-center gap-1.5 rounded-full border border-mb-line bg-white px-3.5 py-2 text-xs font-bold text-destructive transition-colors hover:border-destructive disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Excluir
            </button>
          )}
        </div>
      </header>

      <div className="grid items-start gap-6 p-4 sm:p-8 xl:grid-cols-[1fr_380px]">
        {/* Coluna esquerda — o pedido */}
        <div className="flex flex-col gap-6">
          <AdminCard className="grid gap-6 p-4 sm:p-6 md:grid-cols-[320px_1fr]">
            <PreviewStage items={data.items} className="h-70 rounded-mb-button" />

            <div>
              <div className="flex items-center gap-3">
                <StatusPill status={data.status} />
                {data.payment && (
                  <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold leading-4 text-emerald-800">
                    <BadgeCheck className="h-3.5 w-3.5" />
                    Pago em {formatDate(data.payment.paidAt)} ·{" "}
                    {PAYMENT_METHODS[data.payment.method]}
                  </span>
                )}
                <span className="text-xs text-mb-muted">
                  Criado em {formatDateTime(data.createdAt)}
                </span>
              </div>
              {(data.sentAt || data.approvedAt) && (
                <p className="mt-1.5 text-xs text-mb-muted">
                  {data.sentAt && <>Enviado pelo cliente em {formatDateTime(data.sentAt)}</>}
                  {data.sentAt && data.approvedAt && " · "}
                  {data.approvedAt && <>Aprovado em {formatDateTime(data.approvedAt)}</>}
                </p>
              )}

              <h2 className="mt-3 font-display text-3xl font-bold tabular-nums tracking-tight text-mb-navy sm:text-4xl">
                {formatBRL(data.total)}
              </h2>
              <p className="mt-1 text-sm text-mb-muted">
                {countLabel(pieces, "peça", "peças")} em{" "}
                {countLabel(data.items.length, "item configurado", "itens configurados")}
              </p>

              <p className="mt-6 text-xs font-bold uppercase tracking-[0.1em] text-mb-muted">
                Mudar status
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {STATUS_STEPS.map((step) => (
                  <button
                    key={step}
                    type="button"
                    aria-pressed={step === data.status}
                    disabled={updateStatus.isPending || step === data.status}
                    onClick={() => {
                      if (
                        step === "cancelado" &&
                        !window.confirm(
                          `Cancelar o pedido #${padOrderNumber(data.number)}?

Ele sai de faturamento, custo e lucro. Dá para reabrir depois escolhendo outro status.`,
                        )
                      ) {
                        return;
                      }
                      updateStatus.mutate(
                        { id: data.id, status: step },
                        {
                          onSuccess: () =>
                            toast.success(`Pedido marcado como ${STATUS_LABEL[step]}.`),
                          onError: (cause) =>
                            toast.error(
                              cause instanceof Error
                                ? cause.message
                                : "Não foi possível mudar o status.",
                            ),
                        },
                      );
                    }}
                    className={cn(
                      "rounded-full border px-3.5 py-2 text-xs font-bold transition-colors",
                      step === data.status
                        ? step === "cancelado"
                          ? "border-destructive bg-destructive text-white"
                          : "border-mb-navy bg-mb-navy text-white"
                        : step === "cancelado"
                          ? "border-mb-line bg-white text-mb-muted hover:border-destructive hover:text-destructive"
                          : "border-mb-line bg-white text-mb-muted hover:text-mb-navy",
                      updateStatus.isPending && "opacity-60",
                    )}
                  >
                    {STATUS_LABEL[step]}
                  </button>
                ))}
              </div>
            </div>
          </AdminCard>

          <AdminCard>
            <div className="flex items-center justify-between border-b border-mb-line px-4 py-4 sm:px-6">
              <h2 className="font-display text-base font-bold text-mb-navy">Itens do pedido</h2>
              <span className="text-xs font-bold text-mb-muted">{countLabel(pieces, "peça", "peças")}</span>
            </div>

            {data.items.map((item, index) => (
              <div
                key={`${item.pieceId}-${index}`}
                className="flex flex-wrap items-start gap-x-4 gap-y-2 border-b border-mb-line px-4 py-4 sm:flex-nowrap sm:px-6"
              >
                <ItemPreview item={item} />
                <div className="min-w-0 flex-1 basis-48">
                  <p className="text-sm font-bold text-mb-ink">{item.name}</p>
                  <p className="text-xs leading-4 text-mb-muted text-pretty">
                    {item.optionsSummary}
                  </p>
                  {item.units && (
                    <UnitGridView grid={item.units} className="mt-2.5 text-mb-ink" />
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-bold tabular-nums text-mb-navy">
                    {formatBRL(item.total)}
                  </p>
                  <p className="text-xs tabular-nums text-mb-muted">
                    {item.quantity} × {formatBRL(item.unitPrice)}
                  </p>
                </div>
              </div>
            ))}

            <div className="flex items-center justify-between px-4 py-4 sm:px-6">
              <span className="text-sm font-bold text-mb-ink">Valor total</span>
              <span className="font-display text-xl font-bold tabular-nums text-mb-navy">
                {formatBRL(data.total)}
              </span>
            </div>
          </AdminCard>
        </div>

        {/* Coluna direita — dinheiro (só admin) e o cliente */}
        <div className="flex flex-col gap-6">
          {isAdmin && <FinanceCard orderId={data.id} />}

          <AdminCard className="p-6">
            <h2 className="font-display text-base font-bold text-mb-navy">Cliente</h2>

            <div className="mt-4 flex items-center gap-3">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-mb-surface font-display text-sm font-bold text-mb-navy">
                {initials(data.customer.name)}
              </span>
              <div className="min-w-0">
                <Link
                  to="/admin/clientes/$clienteId"
                  params={{ clienteId: key }}
                  className="truncate text-sm font-bold text-mb-ink hover:text-mb-navy"
                >
                  {data.customer.name}
                </Link>
                {customer.data && (
                  <p className="text-xs text-mb-muted">
                    {customer.data.orders} {customer.data.orders === 1 ? "pedido" : "pedidos"} ·
                    desde {formatDate(customer.data.firstOrderAt)}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-2">
              <div className="flex items-center gap-2 text-sm text-mb-ink">
                <Phone className="h-4 w-4 shrink-0 text-mb-muted" />
                {data.customer.whatsapp}
              </div>
              {data.customer.email && (
                <div className="flex items-center gap-2 text-sm text-mb-ink">
                  <Mail className="h-4 w-4 shrink-0 text-mb-muted" />
                  <span className="truncate">{data.customer.email}</span>
                </div>
              )}
            </div>

            <a
              href={whatsappUrl(data)}
              target="_blank"
              rel="noreferrer"
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-mb-button bg-mb-yellow px-5 py-3.5 font-display text-sm font-bold text-mb-ink shadow-cta transition-transform hover:-translate-y-0.5"
            >
              <MessageCircle className="h-[18px] w-[18px]" />
              Enviar orçamento no WhatsApp
            </a>
            <p className="mt-2 text-center text-[11px] leading-4 text-mb-muted">
              Abre a conversa com o resumo do pedido já escrito.
            </p>
          </AdminCard>

          <AdminCard className="p-6">
            <h2 className="font-display text-base font-bold text-mb-navy">
              Outros pedidos deste cliente
            </h2>

            {otherOrders.length > 0 ? (
              <div className="mt-4 flex flex-col gap-1">
                {otherOrders.map((other) => (
                  <Link
                    key={other.id}
                    to="/admin/pedidos/$pedidoId"
                    params={{ pedidoId: other.id }}
                    className="flex items-center justify-between rounded-mb-field px-3 py-2.5 transition-colors hover:bg-mb-surface"
                  >
                    <div>
                      <p className="font-display text-sm font-bold tabular-nums text-mb-navy">
                        #{padOrderNumber(other.number)}
                      </p>
                      <p className="text-[11px] leading-4 text-mb-muted">
                        {formatDate(other.createdAt)} ·{" "}
                        {countLabel(
                          other.items.reduce((sum, item) => sum + item.quantity, 0),
                          "peça",
                          "peças",
                        )}
                      </p>
                    </div>
                    <span className="text-sm font-bold tabular-nums text-mb-ink">
                      {formatBRL(other.total)}
                    </span>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="mt-4 text-sm text-mb-muted">
                {customerOrders.isLoading ? "Carregando…" : "Este é o primeiro pedido do cliente."}
              </p>
            )}
          </AdminCard>
        </div>
      </div>
    </>
  );
}

const initials = (name: string): string =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
